import { economyFetch as fetch } from '../../lib/economyClient';
/**
 * ショップシステム - サーバーAPI統合版
 *
 * shopSystem.ts と同じインターフェースを提供しつつ、
 * サーバーAPIを使用してカード売却処理を行う
 *
 * 使用方法:
 * import { shopSystemServer } from './shopSystemServer';
 * const result = await shopSystemServer.sellCardAsync(playerData, cardId, rarity, quantity, card);
 */

// ========================================
// Supabase設定（直接REST API使用）
// supabase.functions.invoke() はハングする問題があるため
// ========================================

const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZpa3FrcXdrbGJkamZoa3lkenB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwNjYzODcsImV4cCI6MjA4MTY0MjM4N30.PPIf98mu1HgR95CaymSJiV9E5hnlsMJuQeXw2MNqKsw';
const AUTH_STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';
const API_TIMEOUT_MS = 30000;

function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.access_token || null;
    }
  } catch (err) {
    console.error('[ShopAPI] Failed to get access token:', err);
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation}: タイムアウト（${ms}ms）`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
}

// ========================================
// 型定義
// ========================================

interface SellResult {
  success: boolean;
  error?: string;
  goldEarned?: number;
  modifier?: number;
  breakdown?: Array<{ reason: string; value: number }>;
  playerData?: Record<string, unknown>;
  remainingCount?: number;
}

// ========================================
// サーバーAPI呼び出し
// ========================================

/**
 * サーバーAPIでカードを売却
 */
async function callSellCardAPI(
  cardId: string,
  rarity: string,
  quantity: number
): Promise<{
  success: boolean;
  error?: string;
  goldEarned: number;
  marketPrice: number;
  newGoldBalance: number;
  remainingCount: number;
}> {
  console.log('[ShopAPI] Calling sell card API:', { cardId, rarity, quantity });

  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.error('[ShopAPI] No access token found');
      return {
        success: false,
        error: '認証が必要です。ページをリロードしてください。',
        goldEarned: 0,
        marketPrice: 0,
        newGoldBalance: 0,
        remainingCount: 0,
      };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/collection/sell`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          card_id: cardId,
          rarity,
          quantity,
        }),
      }),
      API_TIMEOUT_MS,
      'カード売却API'
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[ShopAPI] HTTP error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[ShopAPI] Response data:', data);

    if (!data?.success) {
      return {
        success: false,
        error: data?.error || 'カード売却に失敗しました',
        goldEarned: 0,
        marketPrice: 0,
        newGoldBalance: 0,
        remainingCount: 0,
      };
    }

    return {
      success: true,
      goldEarned: data.gold_earned || 0,
      marketPrice: data.market_price || 0,
      newGoldBalance: data.new_gold_balance || 0,
      remainingCount: data.remaining_count || 0,
    };
  } catch (err) {
    console.error('[ShopAPI] Sell card API error:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'サーバーエラー',
      goldEarned: 0,
      marketPrice: 0,
      newGoldBalance: 0,
      remainingCount: 0,
    };
  }
}

// ========================================
// カード売却（サーバーAPI版）
// ========================================

/**
 * カードを売却（サーバーAPI版）
 * @param playerData - プレイヤーデータ
 * @param cardId - カードID
 * @param rarity - レアリティ
 * @param quantity - 売却枚数
 * @param _card - カードデータ（互換性のため、サーバー側で市場価格を取得）
 * @returns { success, error?, goldEarned?, modifier?, playerData?, remainingCount? }
 */
export async function sellCardAsync(
  playerData: Record<string, unknown>,
  cardId: string,
  rarity: string,
  quantity: number = 1,
  _card: Record<string, unknown> = {}
): Promise<SellResult> {
  const apiResult = await callSellCardAPI(cardId, rarity, quantity);

  if (!apiResult.success) {
    return {
      success: false,
      error: apiResult.error,
    };
  }

  // コレクションも更新
  const collection = [...((playerData.collection as Array<Record<string, unknown>>) || [])];
  const cardIndex = collection.findIndex(
    c => c.cardId === cardId && c.rarity === rarity
  );

  if (cardIndex >= 0) {
    if (apiResult.remainingCount > 0) {
      collection[cardIndex] = {
        ...collection[cardIndex],
        quantity: apiResult.remainingCount,
      };
    } else {
      // 0枚になったら削除
      collection.splice(cardIndex, 1);
    }
  }

  // playerDataを更新（サーバーの結果を反映）
  const newPlayerData = {
    ...playerData,
    gold: apiResult.newGoldBalance,
    stats: {
      ...(playerData.stats as Record<string, unknown> || {}),
      totalGoldEarned: ((playerData.stats as Record<string, unknown>)?.totalGoldEarned as number || 0) + apiResult.goldEarned,
    },
    collection,
  };

  return {
    success: true,
    goldEarned: apiResult.goldEarned,
    modifier: 0, // サーバーから詳細な内訳は返していないので0
    breakdown: [],
    playerData: newPlayerData,
    remainingCount: apiResult.remainingCount,
  };
}

// ========================================
// エクスポート
// ========================================

export const shopSystemServer = {
  sellCardAsync,
};

export default shopSystemServer;
